import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { s } from '../helpers';

export interface TopColumnItem {
  key: string;
  label: string;
  tip?: string;
  color: string;
  weight: number;
  icon: string;
  onClick: () => void;
}

export interface ManagerRowItem {
  accountId: string;
  cmName: string;
  alias: string;
  initial: string;
  platform: string;
  platformTag: string;
  stage: string;
  stageColor: string;
  stageBg: string;
  capital: string;
  balance: string;
  pnl: string;
  pnlColor: string;
  dd: string;
  ddColor: string;
  days: number;
  status: string;
  statusColor: string;
  onClick: () => void;
  rank: number;
  medalBg: string;
  medalBorder: string;
  medalColor: string;
  cmId: string;
  level: string;
  payoutMark: string;
  payoutColor: string;
  payoutSize: string;
  discipline: number;
  risk: number | string;
  consistency: number;
  score: number | string;
  riskTip?: string;
  scoreTip?: string;
  trendMark: string;
  trendColor: string;
  trendSize: string;
  trendTitle: string;
}

export interface TopSortOption {
  v: string;
  l: string;
}

export interface TopManagersSectionProps {
  tableTotal: number;
  topCards: boolean;
  topTable: boolean;
  topSortKey: string;
  onTopSort: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  topSortOpts: TopSortOption[];
  topCols: TopColumnItem[];
  rankTip?: string;
  rows: ManagerRowItem[];
  noRows: boolean;
  hasMore: boolean;
  moreH: string;
  moreLabel: string;
  toggleMore: () => void;
  selectedTierCapital?: number | null;
  onClearTierFilter?: () => void;
}

export const TopManagersSection: React.FC<TopManagersSectionProps> = ({
  tableTotal,
  topCards,
  topTable,
  topSortKey,
  onTopSort,
  topSortOpts,
  topCols,
  rankTip,
  rows,
  noRows,
  hasMore,
  moreH,
  moreLabel,
  toggleMore,
  selectedTierCapital,
  onClearTierFilter
}) => {
  const { t } = useTranslation();
  // Header tooltip: fixed-positioned so the table's horizontal scroll container doesn't clip it
  const [colTip, setColTip] = useState<{ text: string; x: number; y: number } | null>(null);
  const [showGuide, setShowGuide] = useState(false);

  const openTip = (text: string | undefined, e: React.MouseEvent<HTMLElement>) => {
    if (!text) return;
    const r = e.currentTarget.getBoundingClientRect();
    const width = 280;
    const x = Math.max(8, Math.min(r.left + r.width / 2 - width / 2, window.innerWidth - width - 8));
    setColTip({ text, x, y: r.bottom + 6 });
  };
  const closeTip = () => setColTip(null);

  // A fixed tooltip would drift away from its header on scroll, so close it
  useEffect(() => {
    if (!colTip) return;
    const close = () => setColTip(null);
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [colTip]);
  const toggleTip = (text: string | undefined, e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation(); // don't trigger the column sort
    if (colTip && colTip.text === text) closeTip();
    else openTip(text, e);
  };

  const infoIcon = (text?: string) =>
    text ? (
      <span
        role="img"
        aria-label={text}
        onMouseEnter={e => openTip(text, e)}
        onMouseLeave={closeTip}
        onClick={e => toggleTip(text, e)}
        style={s(`display:inline-flex;color:#B8A88E;font-size:11px;cursor:help`)}
      >
        <i className="icon-info"></i>
      </span>
    ) : null;

  const guideItems = [{ label: '#', tip: rankTip }, ...(topCols || []).map(c => ({ label: c.label, tip: c.tip }))].filter(g => g.tip);

  return (
    <>
      <section
        style={s(
          `background:#FFFEFB;border:1px solid #EFE6D6;border-radius:12px;box-shadow:0 1px 3px rgba(120,90,30,0.06);min-width:0;padding:16px 18px;display:flex;flex-direction:column;gap:12px`
        )}
      >
        <div style={s(`display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:8px`)}>
          <div style={s(`display:flex;align-items:center;gap:10px;flex-wrap:wrap`)}>
            <h2
              style={s(
                `margin:0;font-size:14px;font-weight:700;color:#1B1B1B;text-transform:uppercase;letter-spacing:0.01em`
              )}
            >
              {t('managerNetwork.top.title', 'Top Capital Managers')}
            </h2>
            {selectedTierCapital ? (
              <span
                style={s(
                  `display:inline-flex;align-items:center;gap:6px;padding:2px 8px;border-radius:12px;background:#FBF3E3;border:1px solid #E6DCCB;font-size:11.5px;font-weight:600;color:#A67A2E`
                )}
              >
                <span>{`Tier: $${selectedTierCapital >= 1000 ? selectedTierCapital / 1000 + 'K' : selectedTierCapital}`}</span>
                {onClearTierFilter && (
                  <button
                    onClick={onClearTierFilter}
                    title={t('managerNetwork.top.clearTierFilter', 'Xóa lọc tier')}
                    style={s(`border:0;background:transparent;cursor:pointer;color:#A67A2E;font-size:11px;padding:0;line-height:1;display:flex;align-items:center`)}
                  >
                    ✕
                  </button>
                )}
              </span>
            ) : null}
          </div>
          <span style={s(`font-size:11.5px;color:#6B6358`)}>
            {t('managerNetwork.top.subtitle', '{{count}} CMs · click a row for detail', { count: tableTotal })}
          </span>
        </div>

        {Boolean(topCards) && (
          <>
            <label
              style={s(
                `position:relative;display:flex;align-items:center;gap:8px;height:40px;border:1px solid #E6DCCB;border-radius:8px;padding:0 10px;background:#fff`
              )}
            >
              <i className="icon-arrow-up-down" style={s(`color:#6B6358`)}></i>
              <select
                value={topSortKey}
                onChange={onTopSort}
                style={s(
                  `flex:1;min-width:0;border:0;outline:0;background:transparent;font-size:13px;color:#1B1B1B;padding-right:22px`
                )}
              >
                {topSortOpts?.map((o, o_idx) => (
                  <React.Fragment key={o_idx}>
                    <option value={o.v}>{o.l}</option>
                  </React.Fragment>
                ))}
              </select>
              <i className="icon-chevron-down" style={s(`position:absolute;right:10px;color:#6B6358;pointer-events:none`)}></i>
            </label>
            {guideItems.length > 0 && (
              <div style={s(`border:1px solid #EFE6D6;border-radius:8px;background:#FBF6EC`)}>
                <button
                  onClick={() => setShowGuide(v => !v)}
                  style={s(
                    `width:100%;border:0;background:transparent;padding:9px 12px;display:flex;align-items:center;justify-content:space-between;font-size:12.5px;font-weight:600;color:#8C6424;cursor:pointer`
                  )}
                >
                  <span style={s(`display:flex;align-items:center;gap:6px`)}>
                    <i className="icon-info"></i>
                    {t('managerNetwork.top.howCalculated', 'How each field is calculated')}
                  </span>
                  <i className={showGuide ? 'icon-chevron-up' : 'icon-chevron-down'}></i>
                </button>
                {showGuide && (
                  <dl style={s(`margin:0;padding:0 12px 10px;display:flex;flex-direction:column;gap:8px`)}>
                    {guideItems.map(g => (
                      <div key={g.label}>
                        <dt style={s(`font-size:12px;font-weight:700;color:#1B1B1B`)}>{g.label}</dt>
                        <dd style={s(`margin:2px 0 0;font-size:11.5px;line-height:1.45;color:#5A5146`)}>{g.tip}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            )}
            <div style={s(`display:flex;flex-direction:column`)}>
              {rows?.map((r, r_idx) => (
                <React.Fragment key={r_idx}>
                  <div
                    onClick={r.onClick}
                    style={s(
                      `display:grid;grid-template-columns:28px minmax(0,1fr) auto;gap:10px;align-items:center;padding:12px 2px;border-bottom:1px solid #F4EEE3;cursor:pointer;min-height:56px`
                    )}
                  >
                    <span
                      style={s(
                        `width:24px;height:24px;border-radius:50%;background:${r.medalBg};border:1.5px solid ${r.medalBorder};color:${r.medalColor};font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center`
                      )}
                    >
                      {r.rank}
                    </span>
                    <div style={s(`display:flex;flex-direction:column;gap:3px;min-width:0`)}>
                      <div style={s(`display:flex;flex-wrap:wrap;align-items:center;gap:4px 8px;font-size:13px`)}>
                        <span style={s(`font-weight:600;color:#1B1B1B`)}>{r.cmId}</span>
                        <span style={s(`color:${r.stageColor};font-weight:500;font-size:12px`)}>{r.stage}</span>
                        <span style={s(`color:#3A342C;font-size:12px`)}>{r.capital}</span>
                        <span style={s(`color:${r.payoutColor};font-weight:700;font-size:12px`)}>{r.payoutMark}</span>
                      </div>
                      <span style={s(`font-size:11.5px;color:#6B6358`)}>
                        {t('managerNetwork.top.mobileStats', 'Discipline {{discipline}} · Risk {{risk}} · Consistency {{consistency}}', {
                          discipline: r.discipline,
                          risk: r.risk,
                          consistency: r.consistency
                        })}
                      </span>
                    </div>
                    <div style={s(`display:flex;flex-direction:column;align-items:flex-end;gap:2px`)}>
                      <span style={s(`font-size:17px;font-weight:700;color:#1B1B1B`)}>{r.score}</span>
                      <span style={s(`color:${r.trendColor};font-size:${r.trendSize};font-weight:700`)}>{r.trendMark}</span>
                    </div>
                  </div>
                </React.Fragment>
              ))}
              {Boolean(noRows) && (
                <div style={s(`padding:24px;text-align:center;color:#6B6358;font-size:13px`)}>
                  {t('managerNetwork.top.noMatch', 'No Capital Managers match the current search and filters.')}
                </div>
              )}
            </div>
          </>
        )}

        {Boolean(topTable) && (
          <div style={s(`overflow-x:auto`)}>
            <div style={s(`min-width:880px`)}>
              <div
                style={s(
                  `display:grid;grid-template-columns:44px minmax(90px,1.1fr) 84px minmax(96px,1.1fr) 72px repeat(3,minmax(80px,1fr)) minmax(104px,1.1fr) 84px;gap:8px;padding:8px 6px;border-bottom:1px solid #EFE6D6;font-size:11.5px;color:#3A342C;text-align:center`
                )}
              >
                <span style={s(`display:flex;align-items:center;justify-content:center;gap:3px`)}>
                  #{infoIcon(rankTip)}
                </span>
                {topCols?.map((c, c_idx) => (
                  <React.Fragment key={c_idx}>
                    <button
                      onClick={c.onClick}
                      style={s(
                        `border:0;background:transparent;padding:0;font-size:11.5px;font-weight:${c.weight};color:${c.color};cursor:pointer;display:flex;align-items:center;justify-content:center;gap:3px;white-space:nowrap`
                      )}
                    >
                      {c.label}
                      <i className={`icon-${c.icon}`} style={s(`font-size:11px`)}></i>
                      {infoIcon(c.tip)}
                    </button>
                  </React.Fragment>
                ))}
              </div>
              {rows?.map((r, r_idx) => (
                <React.Fragment key={r_idx}>
                  <div
                    onClick={r.onClick}
                    title={`${r.cmName} · ${r.alias}`}
                    style={s(
                      `display:grid;grid-template-columns:44px minmax(90px,1.1fr) 84px minmax(96px,1.1fr) 72px repeat(3,minmax(80px,1fr)) minmax(104px,1.1fr) 84px;gap:8px;align-items:center;padding:7px 6px;border-bottom:1px solid #F4EEE3;font-size:12.5px;color:#1B1B1B;text-align:center;cursor:pointer`
                    )}
                  >
                    <span style={s(`display:flex;justify-content:center`)}>
                      <span
                        style={s(
                          `width:22px;height:22px;border-radius:50%;background:${r.medalBg};border:1.5px solid ${r.medalBorder};color:${r.medalColor};font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center`
                        )}
                      >
                        {r.rank}
                      </span>
                    </span>
                    <span>{r.cmId}</span>
                    <span style={s(`color:${r.stageColor};font-weight:500`)}>{r.stage}</span>
                    <span>{r.capital}</span>
                    <span style={s(`color:${r.payoutColor};font-size:${r.payoutSize};font-weight:700`)}>{r.payoutMark}</span>
                    <span>{r.discipline}</span>
                    <span title={r.riskTip}>{r.risk}</span>
                    <span>{r.consistency}</span>
                    <span title={r.scoreTip} style={s(`font-weight:700`)}>{r.score}</span>
                    <span
                      title={r.trendTitle}
                      style={s(`color:${r.trendColor};font-size:${r.trendSize};font-weight:700;letter-spacing:1px`)}
                    >
                      {r.trendMark}
                    </span>
                  </div>
                </React.Fragment>
              ))}
              {Boolean(noRows) && (
                <div style={s(`padding:32px 20px;text-align:center;color:#6B6358;font-size:13px`)}>
                  {t('managerNetwork.top.noMatch', 'No Capital Managers match the current search and filters.')}
                </div>
              )}
            </div>
          </div>
        )}

        {Boolean(hasMore) && (
          <div style={s(`display:flex;justify-content:center`)}>
            <button
              onClick={toggleMore}
              style={s(
                `height:${moreH};padding:0 20px;border:1px solid #E6DCCB;border-radius:6px;background:#fff;color:#3A342C;font-size:12.5px;cursor:pointer;display:inline-flex;align-items:center;gap:6px;font-weight:500;box-shadow:0 1px 2px rgba(0,0,0,0.03)`
              )}
            >
              {(moreLabel.includes('Đang tải') || moreLabel.includes('Loading')) && (
                <i className="icon-refresh" style={s(`animation:spin 1s linear infinite`)}></i>
              )}
              {moreLabel}
            </button>
          </div>
        )}
      </section>
      {colTip && (
        <div
          role="tooltip"
          style={s(
            `position:fixed;left:${colTip.x}px;top:${colTip.y}px;width:280px;z-index:1000;background:#1B2430;color:#F4F1EA;border-radius:8px;padding:9px 11px;font-size:11.5px;line-height:1.5;font-weight:400;text-align:left;box-shadow:0 8px 24px rgba(0,0,0,0.18);pointer-events:none;white-space:normal`
          )}
        >
          {colTip.text}
        </div>
      )}
      <div style={s(`font-size:11.5px;color:#9AA3AF;padding:4px 0 12px`)}>
        {t('managerNetwork.top.connectedApi', 'Connected to FTMO API: /capital-managers, /capital-managers/dashboard, /capital-managers/detail')}
      </div>
    </>
  );
};
